"use client";

import React from "react";
import { ArrowRight, Building2, CircleCheck as CheckCircle2, Compass, Hotel, TreePalm as Palmtree, TrendingUp } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface MetricSegment {
  icon: React.ElementType;
  title: string;
  metric: string;
  studies: string;
}
const METRIC_SEGMENTS: MetricSegment[] = [{
  icon: Palmtree,
  title: "Resorts & Leisure",
  metric: "+34% ADR in peak seasons",
  studies: "18 studies"
}, {
  icon: Compass,
  title: "Boutique & Heritage",
  metric: "+42% direct booking shift",
  studies: "24 studies"
}, {
  icon: Building2,
  title: "Business & City Hotels",
  metric: "+28% corporate RevPAR",
  studies: "31 studies"
}];
const CHECKLIST_PILLS = ["Audited performance benchmarks", "Segment-specific pricing playbooks", "Direct OTA commission reductions"];
export default function CaseLibraryHero() {
  return <section id="caselibraryhero" data-nav="light" className="relative overflow-hidden bg-background text-foreground pt-14 pb-16 lg:pt-20 lg:pb-24 border-b border-border/60">
      {/* Subtle Background Pattern Kit */}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-40" aria-hidden="true">
        <div className="absolute top-0 right-1/4 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute bottom-0 left-10 h-72 w-72 rounded-full bg-secondary/5 blur-2xl" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          {/* Left Column: Hero Content & Actions */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6">
            <div className="flex flex-wrap items-center gap-2.5">
              <Badge variant="outline" className="px-3 py-1 font-medium tracking-wide uppercase text-[11px] rounded-full border-secondary/30 bg-secondary/10 text-secondary-foreground">
                HotelRevBaba Case Library
              </Badge>
            </div>

            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.2]">
              Proven revenue growth across independent hotels and luxury resorts.
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Explore how hands-on OTA distribution, structured rate audits, and modern hotel tech unlocked double-digit RevPAR increases for owners and general managers across India.
            </p>

            {/* Checklist Pills */}
            <div className="flex flex-wrap gap-2.5 pt-1">
              {CHECKLIST_PILLS.map((pill, i) => <div key={pill} data-index={i} className="inline-flex items-center gap-2 rounded-lg border border-border/80 bg-card/80 px-3.5 py-2 text-xs sm:text-sm font-medium text-foreground shadow-2xs">
                  <CheckCircle2 className="size-4 text-secondary shrink-0" />
                  <span>{pill}</span>
                </div>)}
            </div>

            {/* Action CTAs */}
            <div className="flex flex-wrap items-center gap-4 pt-3">
              <Button asChild size="lg" className="rounded-md font-semibold px-6 shadow-sm group">
                <Link to="#">
                  <span>Book a consultation</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md font-semibold px-6 bg-card border-border hover:bg-muted/50">
                <Link to="#">
                  <span>Explore our services</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Performance Summary Card */}
          <div className="lg:col-span-5 w-full">
            <Card className="border border-border/80 bg-card shadow-md relative overflow-hidden">
              {/* Top Card Accent */}
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-secondary/80 via-secondary to-secondary/60" />
              
              <CardHeader className="pb-4 pt-6">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                      Performance by property type
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Field-tested frameworks led by Sunil Singh
                    </p>
                  </div>
                  <div className="size-9 rounded-full bg-secondary/15 flex items-center justify-center shrink-0">
                    <TrendingUp className="size-4 text-secondary-foreground" />
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-3 pb-5">
                {METRIC_SEGMENTS.map((segment, i) => {
                const Icon = segment.icon;
                return <div key={segment.title} data-index={i} className="group relative flex items-center justify-between p-3.5 rounded-lg border border-border/60 bg-muted/20 hover:bg-muted/40 transition-colors">
                      <div className="flex items-center gap-3.5">
                        <div className="size-9 rounded-md bg-secondary/10 flex items-center justify-center text-secondary shrink-0">
                          <Icon className="size-4.5" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">
                            {segment.title}
                          </p>
                          <p className="text-xs font-medium text-secondary-foreground font-mono">
                            {segment.metric}
                          </p>
                        </div>
                      </div>

                      <Badge variant="secondary" className="text-[11px] font-normal px-2.5 py-0.5 border border-border/50 bg-background text-muted-foreground">
                        {segment.studies}
                      </Badge>
                    </div>;
              })}
              </CardContent>

              <Separator />

              <CardFooter className="py-4 bg-muted/15 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  Average OTA revenue uplift achieved:
                </span>
                <span className="font-mono text-sm sm:text-base font-bold text-secondary-foreground bg-secondary/15 px-2.5 py-0.5 rounded border border-secondary/30">
                  +27.4%
                </span>
              </CardFooter>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}